import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const testimonials = [
  {
    name: "Sarah Johnson",
    role: "Software Engineer",
    text: "ImproveResume helped me land my dream job! The AI-powered suggestions made my resume stand out from the crowd.",
    image: "https://randomuser.me/api/portraits/women/1.jpg"
  },
  {
    name: "Michael Chen",
    role: "Product Manager",
    text: "I was amazed by how quickly I could optimize my resume for different job applications. Definitely worth it!",
    image: "https://randomuser.me/api/portraits/men/2.jpg"
  },
  {
    name: "Emily Rodriguez",
    role: "Marketing Director",
    text: "The ATS optimization feature is a game-changer. I started getting more interview calls almost immediately.",
    image: "https://randomuser.me/api/portraits/women/3.jpg"
  },
  {
    name: "David Kim",
    role: "Data Scientist",
    text: "The AI suggestions for technical skills were spot-on. It helped me highlight my expertise in machine learning projects perfectly.",
    image: "https://randomuser.me/api/portraits/men/4.jpg"
  },
  {
    name: "Rachel Thompson",
    role: "UX Designer",
    text: "As a creative professional, I was skeptical at first. But ImproveResume helped me balance my design portfolio with ATS-friendly content.",
    image: "https://randomuser.me/api/portraits/women/5.jpg"
  },
  {
    name: "James Wilson",
    role: "Sales Manager",
    text: "The industry-specific keywords suggestion feature is brilliant. My interview rate has doubled since using ImproveResume!",
    image: "https://randomuser.me/api/portraits/men/6.jpg"
  }
];

const Landing = ({ onGetStarted }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((current) => 
        current === testimonials.length - 1 ? 0 : current + 1
      );
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img 
                  src="/TCAI_logo.jpg" 
                  alt="TCAI Logo" 
                  className="h-16 w-auto"
                />
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link 
                to="/pricing" 
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Pricing
              </Link>
            <button
              onClick={onGetStarted}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Sign In
            </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-semibold text-gray-900 mb-4">
            Your Career Success Platform!
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Optimize your resume and ace your interviews with AI-powered tools designed for your success.
          </p>
        </div>

        {/* Resume Feature Cards */}
        <div className="mb-16">
          <h3 className="text-2xl font-medium text-center mb-8">Resume Optimization</h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Card 1: Highlight Skills */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all hover:translate-y-[-2px]">
              <div className="text-center">
                <div className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Highlight Skills</h3>
                <p className="text-gray-600 text-sm">
                  Automatically emphasize your most relevant skills based on the job description.
                </p>
              </div>
            </div>

            {/* Card 2: ATS Friendly */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all hover:translate-y-[-2px]">
              <div className="text-center">
                <div className="bg-indigo-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">ATS Friendly</h3>
                <p className="text-gray-600 text-sm">
                  Optimized to pass automated Applicant Tracking Systems.
                </p>
              </div>
            </div>

            {/* Card 3: Stand Out */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all hover:translate-y-[-2px]">
              <div className="text-center">
                <div className="bg-purple-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Stand Out</h3>
                <p className="text-gray-600 text-sm">
                  Increase your chances of getting that interview
                </p>
              </div>
            </div>

            {/* Card 4: Save Time */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all hover:translate-y-[-2px]">
              <div className="text-center">
                <div className="bg-green-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Save Time</h3>
                <p className="text-gray-600 text-sm">
                  Tailor your resume in minutes, not hours!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mock Interview Feature Cards */}
        <div className="mb-16">
          <h3 className="text-2xl font-medium text-center mb-8">Mock Interviews</h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Card 1: AI-Powered Practice */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all hover:translate-y-[-2px]">
              <div className="text-center">
                <div className="bg-orange-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">AI-Powered Practice</h3>
                <p className="text-gray-600 text-sm">
                  Practice with intelligent AI interviewers that adapt to your responses.
                </p>
              </div>
            </div>

            {/* Card 2: Real-time Feedback */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all hover:translate-y-[-2px]">
              <div className="text-center">
                <div className="bg-teal-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Real-time Feedback</h3>
                <p className="text-gray-600 text-sm">
                  Get instant feedback on your answers and communication skills.
                </p>
              </div>
            </div>

            {/* Card 3: Industry-Specific Questions */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all hover:translate-y-[-2px]">
              <div className="text-center">
                <div className="bg-pink-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Industry-Specific Questions</h3>
                <p className="text-gray-600 text-sm">
                  Practice with questions tailored to your industry and role.
                </p>
              </div>
            </div>

            {/* Card 4: Build Confidence */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-all hover:translate-y-[-2px]">
              <div className="text-center">
                <div className="bg-yellow-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Build Confidence</h3>
                <p className="text-gray-600 text-sm">
                  Gain confidence through repeated practice and performance tracking.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* video section */}
        {/* <div className="mb-16">
          <h3 className="text-2xl font-medium text-center mb-6">See How It Works</h3>
          <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden shadow-lg">
            <iframe
              className="w-full h-[500px]"
              src="https://www.youtube.com/embed/jT0XpAGuick"
              title="How to Use Resume Tailor"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div> */} 

        {/* Call to Action */}
        <div className="text-center mb-16">
          <button
            onClick={onGetStarted}
            className="inline-flex items-center px-6 py-3 border border-transparent text-lg font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Try Now
          </button>
        </div>

        {/* Reviews Section */}
        <div className="mb-12">
          <h3 className="text-2xl font-medium text-center mb-12">What Our Users Say</h3>
          
          <div className="relative max-w-2xl mx-auto">
            {/* Slider Container */}
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ 
                  transform: `translateX(-${currentSlide * 100}%)`,
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={index}
                    className="w-full flex-shrink-0"
                  >
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center mb-4">
                        <img 
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                        <div className="ml-4">
                          <h4 className="font-medium">{testimonial.name}</h4>
                          <p className="text-sm text-gray-500">{testimonial.role}</p>
                        </div>
                      </div>
                      <p className="text-gray-600 italic">"{testimonial.text}"</p>
                      <div className="mt-4 flex text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentSlide(current => (current > 0 ? current - 1 : testimonials.length - 1))}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-50 -ml-4"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => setCurrentSlide(current => (current < testimonials.length - 1 ? current + 1 : 0))}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-50 -mr-4"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Navigation Dots */}
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 w-2 rounded-full transition-colors duration-200 ${
                    currentSlide === index ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing; 